<script setup>
import { ref, watch, computed, useSlots } from "vue";
import { Switch, SwitchDescription, SwitchGroup, SwitchLabel } from "@headlessui/vue";
import { useI18n } from "vue-i18n";
import { twMerge } from "tailwind-merge";
import { FormHelp } from ".";

const props = defineProps({
    modelValue: {
        type: Boolean,
        default: false,
    },
    label: {
        type: String,
        required: false,
    },
    help: {
        type: String,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    size: {
        type: String,
    },
    z: {
        type: Number,
        default: 0,
    },
});

const emit = defineEmits(["update:modelValue"]);

const { t: __ } = useI18n();
const slots = useSlots();
const enabled = ref(props.modelValue);

const switchClasses = computed(() => {
    let classes = [
        "cursor-pointer relative inline-flex flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2",
    ];

    if (props.size === "small") {
        classes.push("h-4 w-8");
    } else {
        classes.push("h-6 w-11");
    }

    if (enabled.value) {
        classes.push("bg-gray-800 focus:ring-gray-900");

        switch (props.z) {
            case 3:
                classes.push("dark:bg-gray-500 dark:focus:ring-gray-400 dark:focus:ring-offset-gray-700");
                break;
            case 2:
                classes.push("dark:bg-gray-600 dark:focus:ring-gray-500 dark:focus:ring-offset-gray-800");
                break;
            case 1:
                classes.push("dark:bg-gray-700 dark:focus:ring-gray-600 dark:focus:ring-offset-gray-900");
                break;
            default:
                classes.push("dark:bg-gray-800 dark:focus:ring-gray-700 dark:focus:ring-offset-gray-950");
                break;
        }
    } else {
        switch (props.z) {
            case 3:
                classes.push("bg-gray-300 focus:ring-gray-900");
                break;
            default:
                classes.push("bg-gray-200 focus:ring-gray-900");
                break;
        }

        switch (props.z) {
            case 3:
                classes.push("dark:bg-gray-300 dark:focus:ring-gray-400 dark:focus:ring-offset-gray-700");
                break;
            case 2:
                classes.push("dark:bg-gray-300 dark:focus:ring-gray-500 dark:focus:ring-offset-gray-800");
                break;
            case 1:
                classes.push("dark:bg-gray-300 dark:focus:ring-gray-600 dark:focus:ring-offset-gray-900");
                break;
            default:
                classes.push("dark:bg-gray-300 dark:focus:ring-gray-700 dark:focus:ring-offset-gray-950");
                break;
        }
    }

    return twMerge(classes);
});

watch(enabled, () => {
    emit("update:modelValue", enabled.value);
});

watch(
    () => props.modelValue,
    () => {
        enabled.value = props.modelValue;
    }
);
</script>

<template>
    <SwitchGroup as="div" class="flex">
        <Switch v-model="enabled" :class="switchClasses">
            <span
                :class="[
                    enabled ? (size === 'small' ? 'translate-x-4' : 'translate-x-5') : 'translate-x-0',
                    size === 'small' ? 'h-3 w-3' : ' h-5 w-5',
                    'pointer-events-none relative inline-block transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                ]">
                <span
                    :class="[
                        enabled ? 'opacity-0 duration-100 ease-out' : 'opacity-100 duration-200 ease-in',
                        'absolute inset-0 flex h-full w-full items-center justify-center transition-opacity',
                    ]"
                    aria-hidden="true">
                    <svg class="w-3 h-3 text-gray-300" fill="none" viewBox="0 0 12 12">
                        <path d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                </span>
                <span
                    :class="[
                        enabled ? 'opacity-100 duration-200 ease-in' : 'opacity-0 duration-100 ease-out',
                        'absolute inset-0 flex h-full w-full items-center justify-center transition-opacity',
                    ]"
                    aria-hidden="true">
                    <svg class="w-3 h-3 text-gray-900" fill="currentColor" viewBox="0 0 12 12">
                        <path
                            d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z" />
                    </svg>
                </span>
            </span>
        </Switch>
        <div :class="[size === 'small' ? 'ml-2' : 'ml-3']">
            <SwitchLabel
                v-if="!!slots.default || label"
                as="span"
                :class="['text-gray-900 dark:text-white', 'block text-sm font-medium leading-6', size === 'small' ? 'text-xs' : 'text-sm', 'cursor-pointer']">
                <slot>{{ label }}</slot>
            </SwitchLabel>
            <slot name="help">
                <SwitchDescription v-if="help" :as="FormHelp" class="mt-0.5">
                    {{ help }}
                </SwitchDescription>
            </slot>
        </div>
    </SwitchGroup>
</template>
